import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AccessibilityScan,
  AccessibilityScanTarget,
  GetAccessibilityScanApplicationErrors,
  GetLatestAccessibilityScanApplicationErrors,
  GetLatestAccessibilityScanResponse,
  ListAccessibilityScanFindingsApplicationErrors,
  ListAccessibilityScanFindingsOptions,
  ListAccessibilityScanFindingsResponse,
  ListAccessibilityScanPageCollectionsOptions,
  ListAccessibilityScanPageCollectionsResponse,
  ListAccessibilityScanPageSummariesApplicationErrors,
  ListAccessibilityScanPageSummariesOptions,
  ListAccessibilityScanPageSummariesResponse,
  RunAccessibilityScanApplicationErrors,
  RunAccessibilityScanOptions,
  RunAccessibilityScanResponse,
  getAccessibilityScan as universalGetAccessibilityScan,
  getLatestAccessibilityScan as universalGetLatestAccessibilityScan,
  listAccessibilityScanFindings as universalListAccessibilityScanFindings,
  listAccessibilityScanPageCollections as universalListAccessibilityScanPageCollections,
  listAccessibilityScanPageSummaries as universalListAccessibilityScanPageSummaries,
  runAccessibilityScan as universalRunAccessibilityScan,
} from './accessibility-v1-accessibility-scan-accessibility-scans.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/accessibility' };

export function runAccessibilityScan(
  httpClient: HttpClient
): RunAccessibilityScanSignature {
  return (
    target: AccessibilityScanTarget,
    options: NonNullablePaths<RunAccessibilityScanOptions, `idempotencyKey`, 0>
  ) =>
    universalRunAccessibilityScan(
      target,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RunAccessibilityScanSignature {
  /**
   * Queues an asynchronous accessibility scan.
   *
   * Retrying with the same `idempotencyKey` for the same site returns the
   * original scan. Reusing the key with a different target fails.
   *
   * Fails if the target is invalid or unavailable, another scan for the same
   * target is active, the idempotency key was reused for another target, or
   * the supported scan size is exceeded. Multi-page targets are never silently
   * sampled or truncated. Run requests are limited to 5 per minute per Wix
   * site and can return HTTP 429. Retry the same intended scan with the same
   * idempotency key.
   * @param - Full-site, page, or supported page-collection target.
   */
  (
    target: AccessibilityScanTarget,
    options: NonNullablePaths<RunAccessibilityScanOptions, `idempotencyKey`, 0>
  ): Promise<
    NonNullablePaths<
      RunAccessibilityScanResponse,
      | `accessibilityScan._id`
      | `accessibilityScan.target.scope`
      | `accessibilityScan.target.page.pageId`
      | `accessibilityScan.target.page.pageUrl`
      | `accessibilityScan.target.pageCollection.collectionId`
      | `accessibilityScan.target.targetType`
      | `accessibilityScan.status`
      | `accessibilityScan.progress.discoveredPageCount`
      | `accessibilityScan.progress.processedPageCount`
      | `accessibilityScan.progress.failedPageCount`
      | `accessibilityScan.progress.pendingPageCount`
      | `accessibilityScan.summary.findingCount`
      | `accessibilityScan.summary.scannedPageCount`
      | `accessibilityScan.summary.affectedPageCount`
      | `accessibilityScan.summary.findingsBySeverity`
      | `accessibilityScan.summary.findingsBySeverity.${number}.severity`
      | `accessibilityScan.summary.findingsBySeverity.${number}.count`
      | `accessibilityScan.summary.findingsByCategory`
      | `accessibilityScan.summary.findingsByCategory.${number}.category`
      | `accessibilityScan.summary.findingsByCategory.${number}.count`
      | `accessibilityScan.summary.coverage.checkedCategories`
      | `accessibilityScan.summary.coverage.checkedRuleIds`
      | `accessibilityScan.summary.siteLevelFindingCount`
      | `accessibilityScan.failure.code`
      | `accessibilityScan.failure.message`
      | `accessibilityScan.revision`
      | `suggestedPollIntervalSeconds`,
      4
    > & {
      __applicationErrorsType?: RunAccessibilityScanApplicationErrors;
    }
  >;
}

export function listAccessibilityScanPageCollections(
  httpClient: HttpClient
): ListAccessibilityScanPageCollectionsSignature {
  return (options?: ListAccessibilityScanPageCollectionsOptions) =>
    universalListAccessibilityScanPageCollections(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListAccessibilityScanPageCollectionsSignature {
  /**
   * Lists the supported page collections currently available on the
   * authenticated site. Use the returned collection ID unchanged when the
   * user asks to scan a page type such as all products, blog posts, booking
   * services, events, or restaurant menu pages.
   */
  (options?: ListAccessibilityScanPageCollectionsOptions): Promise<
    NonNullablePaths<
      ListAccessibilityScanPageCollectionsResponse,
      | `pageCollections`
      | `pageCollections.${number}.collectionId`
      | `pageCollections.${number}.displayName`,
      2
    >
  >;
}

export function listAccessibilityScanPageSummaries(
  httpClient: HttpClient
): ListAccessibilityScanPageSummariesSignature {
  return (
    accessibilityScanId: string,
    options?: ListAccessibilityScanPageSummariesOptions
  ) =>
    universalListAccessibilityScanPageSummaries(
      accessibilityScanId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListAccessibilityScanPageSummariesSignature {
  /**
   * Lists per-page aggregate results for a completed or partially completed
   * scan. The response includes every discovered page: affected pages, pages
   * with no findings, and pages that failed to scan. A page scan returns at
   * most one page summary. Results are available only for completed and
   * partially completed scans; a failed or expired scan never returns an empty
   * list that could be mistaken for a clean result. Affected pages are returned
   * first, ordered by their highest finding severity (critical first) and then
   * finding count. Failed and clear pages follow in stable page-URL order.
   * @param - Completed or partially completed scan ID.
   */
  (
    accessibilityScanId: string,
    options?: ListAccessibilityScanPageSummariesOptions
  ): Promise<
    NonNullablePaths<
      ListAccessibilityScanPageSummariesResponse,
      | `pageSummaries`
      | `pageSummaries.${number}.page.pageId`
      | `pageSummaries.${number}.page.pageUrl`
      | `pageSummaries.${number}.page.title`
      | `pageSummaries.${number}.page.pageCollection.collectionId`
      | `pageSummaries.${number}.page.pageCollection.itemId`
      | `pageSummaries.${number}.status`
      | `pageSummaries.${number}.findingCount`
      | `pageSummaries.${number}.failure.code`
      | `pageSummaries.${number}.failure.message`,
      4
    > & {
      __applicationErrorsType?: ListAccessibilityScanPageSummariesApplicationErrors;
    }
  >;
}

export function getLatestAccessibilityScan(
  httpClient: HttpClient
): GetLatestAccessibilityScanSignature {
  return (target: AccessibilityScanTarget) =>
    universalGetLatestAccessibilityScan(
      target,
      // @ts-ignore
      { httpClient }
    );
}

interface GetLatestAccessibilityScanSignature {
  /**
   * Retrieves the latest stored scan for the requested target without
   * starting a new scan. Fails if the target is invalid or has no stored scan.
   * @param - Target whose latest stored scan should be returned.
   */
  (target: AccessibilityScanTarget): Promise<
    NonNullablePaths<
      GetLatestAccessibilityScanResponse,
      | `accessibilityScan._id`
      | `accessibilityScan.target.scope`
      | `accessibilityScan.target.page.pageId`
      | `accessibilityScan.target.page.pageUrl`
      | `accessibilityScan.target.pageCollection.collectionId`
      | `accessibilityScan.target.targetType`
      | `accessibilityScan.status`
      | `accessibilityScan.progress.discoveredPageCount`
      | `accessibilityScan.progress.processedPageCount`
      | `accessibilityScan.progress.failedPageCount`
      | `accessibilityScan.progress.pendingPageCount`
      | `accessibilityScan.summary.findingCount`
      | `accessibilityScan.summary.scannedPageCount`
      | `accessibilityScan.summary.affectedPageCount`
      | `accessibilityScan.summary.findingsBySeverity`
      | `accessibilityScan.summary.findingsBySeverity.${number}.severity`
      | `accessibilityScan.summary.findingsBySeverity.${number}.count`
      | `accessibilityScan.summary.findingsByCategory`
      | `accessibilityScan.summary.findingsByCategory.${number}.category`
      | `accessibilityScan.summary.findingsByCategory.${number}.count`
      | `accessibilityScan.summary.coverage.checkedCategories`
      | `accessibilityScan.summary.coverage.checkedRuleIds`
      | `accessibilityScan.summary.siteLevelFindingCount`
      | `accessibilityScan.failure.code`
      | `accessibilityScan.failure.message`
      | `accessibilityScan.revision`,
      4
    > & {
      __applicationErrorsType?: GetLatestAccessibilityScanApplicationErrors;
    }
  >;
}

export function getAccessibilityScan(
  httpClient: HttpClient
): GetAccessibilityScanSignature {
  return (accessibilityScanId: string) =>
    universalGetAccessibilityScan(
      accessibilityScanId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetAccessibilityScanSignature {
  /**
   * Retrieves a scan and its latest status.
   *
   * Poll this method until the scan reaches a terminal state. REST callers
   * wait for the positive `suggestedPollIntervalSeconds` value returned in the
   * response. SDK callers poll every 5 seconds because the generated get
   * method returns the scan entity directly. If the retained scan record
   * expired, start a new scan.
   * @param - Scan ID returned by [Run Accessibility Scan](https://dev.wix.com/docs/api-reference/site/accessibility/accessibility-scans-v1/run-accessibility-scan).
   * @returns Current scan state and any available aggregate result.
   */
  (accessibilityScanId: string): Promise<
    NonNullablePaths<
      AccessibilityScan,
      | `_id`
      | `target.scope`
      | `target.page.pageId`
      | `target.page.pageUrl`
      | `target.pageCollection.collectionId`
      | `target.targetType`
      | `status`
      | `progress.discoveredPageCount`
      | `progress.processedPageCount`
      | `progress.failedPageCount`
      | `progress.pendingPageCount`
      | `summary.findingCount`
      | `summary.scannedPageCount`
      | `summary.affectedPageCount`
      | `summary.findingsBySeverity`
      | `summary.findingsBySeverity.${number}.severity`
      | `summary.findingsBySeverity.${number}.count`
      | `summary.findingsByCategory`
      | `summary.findingsByCategory.${number}.category`
      | `summary.findingsByCategory.${number}.count`
      | `summary.coverage.checkedCategories`
      | `summary.coverage.checkedRuleIds`
      | `summary.siteLevelFindingCount`
      | `failure.code`
      | `failure.message`
      | `revision`,
      3
    > & {
      __applicationErrorsType?: GetAccessibilityScanApplicationErrors;
    }
  >;
}

export function listAccessibilityScanFindings(
  httpClient: HttpClient
): ListAccessibilityScanFindingsSignature {
  return (
    accessibilityScanId: string,
    options?: ListAccessibilityScanFindingsOptions
  ) =>
    universalListAccessibilityScanFindings(
      accessibilityScanId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListAccessibilityScanFindingsSignature {
  /**
   * Lists actionable findings for a completed or partially completed scan.
   * Findings are cursor-paged and ordered by severity (critical, serious,
   * moderate, minor, then info), page URL, and finding ID. They may be
   * filtered by page, rule, severity, or user-intent category. Results are
   * available only for completed and partially completed scans; a failed or
   * expired scan never returns an empty list that could be mistaken for no
   * accessibility issues.
   * @param - Completed or partially completed scan ID.
   */
  (
    accessibilityScanId: string,
    options?: ListAccessibilityScanFindingsOptions
  ): Promise<
    NonNullablePaths<
      ListAccessibilityScanFindingsResponse,
      | `findings`
      | `findings.${number}._id`
      | `findings.${number}.accessibilityScanId`
      | `findings.${number}.ruleId`
      | `findings.${number}.title`
      | `findings.${number}.severity`
      | `findings.${number}.page.pageId`
      | `findings.${number}.page.pageUrl`
      | `findings.${number}.page.title`
      | `findings.${number}.page.pageCollection.collectionId`
      | `findings.${number}.page.pageCollection.itemId`
      | `findings.${number}.element.elementId`
      | `findings.${number}.element.selector`
      | `findings.${number}.element.description`
      | `findings.${number}.description`
      | `findings.${number}.remediationSummary`
      | `findings.${number}.helpUrl`
      | `findings.${number}.fixGuidance.category`
      | `findings.${number}.fixGuidance.humanInputRequired`
      | `findings.${number}.fixGuidance.humanInputReason`,
      4
    > & {
      __applicationErrorsType?: ListAccessibilityScanFindingsApplicationErrors;
    }
  >;
}

export {
  AccessibilityElementReference,
  AccessibilityFinding,
  AccessibilityFindingCategory,
  AccessibilityFindingCategoryCount,
  AccessibilityFindingFilter,
  AccessibilityFixCategory,
  AccessibilityFixGuidance,
  AccessibilityPageCollectionReference,
  AccessibilityPageReference,
  AccessibilityPageScanStatus,
  AccessibilityScan,
  AccessibilityScanCoverage,
  AccessibilityScanFailure,
  AccessibilityScanFailureCode,
  AccessibilityScanPageCollection,
  AccessibilityScanPageSummary,
  AccessibilityScanProgress,
  AccessibilityScanScope,
  AccessibilityScanStateUnknownError,
  AccessibilityScanStatus,
  AccessibilityScanSummary,
  AccessibilityScanTarget,
  AccessibilityScanTargetTargetOneOf,
  AccessibilitySeverity,
  AccessibilitySeverityCount,
  CursorPaging,
  CursorPagingMetadata,
  Cursors,
  ExistingAccessibilityScanError,
  GetAccessibilityScanRequest,
  GetAccessibilityScanResponse,
  GetLatestAccessibilityScanRequest,
  GetLatestAccessibilityScanResponse,
  ListAccessibilityScanFindingsOptions,
  ListAccessibilityScanFindingsRequest,
  ListAccessibilityScanFindingsResponse,
  ListAccessibilityScanPageCollectionsOptions,
  ListAccessibilityScanPageCollectionsRequest,
  ListAccessibilityScanPageCollectionsResponse,
  ListAccessibilityScanPageSummariesOptions,
  ListAccessibilityScanPageSummariesRequest,
  ListAccessibilityScanPageSummariesResponse,
  PageCollectionScanTarget,
  PageScanTarget,
  PageScanTargetIdentifierOneOf,
  RunAccessibilityScanOptions,
  RunAccessibilityScanRequest,
  RunAccessibilityScanResponse,
  ScanSizeLimitExceededError,
  TargetType,
  WcagCriterion,
  WcagLevel,
} from './accessibility-v1-accessibility-scan-accessibility-scans.universal.js';
